// SPDX-License-Identifier: BUSL-1.1

/**
 * Unified Agents page - Framework-agnostic view of all agents.
 *
 * Displays agents from ops_agents table regardless of SDK:
 * - pi.dev agents
 * - Self-hosted runners (PR reviewer, etc.)
 * - Claude Code agents
 * - Anthropic Managed Agents
 *
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { apiFetch } from '@/lib/api-client';
import { DataTable } from '@/components/data-table';
import { AgentListSkeleton } from '@/components/skeletons';
import { DerivedAgentHealthBadge } from '@/components/badges';

interface UnifiedAgent {
  id: string;
  agentId: string;
  sdk: string;
  name: string;
  version: string | null;
  status: 'idle' | 'running' | 'stopped' | 'error';
  policy: string;
  budgetMonthly: number | null;
  budgetSpentThisMonth: string | null;
  source: string | null;
  triggers: string[];
  createdAt: string;
  updatedAt: string;
  lastHeartbeat: string | null;
}

interface JobsSummary {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

/* ── Agent Type Badge ────────────────────────────────────────────────────── */

function SdkBadge({ sdk }: { sdk: string }) {
  const colors: Record<string, string> = {
    'pi': 'bg-purple-900/50 text-purple-300',
    'claude-code': 'bg-blue-900/50 text-blue-300',
    'anthropic-ma': 'bg-orange-900/50 text-orange-300',
    'aider': 'bg-green-900/50 text-green-300',
    'custom': 'bg-neutral-700 text-neutral-300',
  };
  
  const labels: Record<string, string> = {
    'pi': 'PI',
    'claude-code': 'Claude Code',
    'anthropic-ma': 'Anthropic MA',
    'aider': 'Aider',
    'custom': 'Custom',
  };
  
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colors[sdk] || colors.custom}`}>
      {labels[sdk] || sdk}
    </span>
  );
}

/* ── Status Badge ────────────────────────────────────────────────────────── */

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    'idle': 'bg-yellow-900/40 text-yellow-400',
    'running': 'bg-green-900/40 text-green-400',
    'stopped': 'bg-neutral-700 text-neutral-400',
    'error': 'bg-red-900/40 text-red-400',
  };
  
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colors[status] || colors.idle}`}>
      {status}
    </span>
  );
}

/* ── Agent List ──────────────────────────────────────────────────────────── */

function UnifiedAgentsTab() {
  const [filter, setFilter] = useState<string>('');
  
  const agents = useQuery({
    queryKey: ['agents', 'unified'],
    queryFn: () => apiFetch<{ data: UnifiedAgent[] }>('/agents'),
    refetchInterval: 30000, // Refresh every 30s
  });

  const jobs = useQuery({
    queryKey: ['jobs', 'summary'],
    queryFn: () => apiFetch<{ data: JobsSummary }>('/agents/jobs/summary'),
    refetchInterval: 10000, // Refresh every 10s
  });

  const isLoading = agents.isLoading || jobs.isLoading;

  if (isLoading) return <AgentListSkeleton />;

  const agentData = agents.data?.data ?? [];
  const jobsData = jobs.data?.data;
  
  // Filter agents
  const filteredAgents = filter
    ? agentData.filter(a => 
        a.name.toLowerCase().includes(filter.toLowerCase()) ||
        a.agentId.toLowerCase().includes(filter.toLowerCase()) ||
        a.sdk.toLowerCase().includes(filter.toLowerCase())
      )
    : agentData;
  
  const runningCount = agentData.filter(a => a.status === 'running').length;

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <SummaryCard
          title="Total Agents"
          value={String(agentData.length)}
          sub="Registered agents"
        />
        <SummaryCard
          title="Running"
          value={String(runningCount)}
          sub="Currently active"
        />
        <SummaryCard
          title="Pending Jobs"
          value={String(jobsData?.pending ?? 0)}
          sub="Awaiting processing"
        />
        <SummaryCard
          title="Completed"
          value={String(jobsData?.completed ?? 0)}
          sub="All time"
        />
      </div>

      {/* Filter */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Filter agents..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Agents Table */}
      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: 'Agent',
            cell: ({ row }) => (
              <Link 
                to={`/agents/${row.original.agentId}`}
                className="block hover:text-blue-400 transition-colors"
              >
                <div className="font-medium text-neutral-100">{row.original.name}</div>
                <div className="text-xs text-neutral-500">{row.original.agentId}</div>
              </Link>
            ),
          },
          {
            accessorKey: 'sdk',
            header: 'SDK',
            cell: ({ row }) => <SdkBadge sdk={row.original.sdk} />,
          },
          {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.status} />,
          },
          {
            id: 'health',
            header: 'Health',
            cell: ({ row }) => (
              <DerivedAgentHealthBadge
                agent={{
                  status: row.original.status,
                  lastHeartbeat: row.original.lastHeartbeat,
                }}
              />
            ),
          },
          {
            accessorKey: 'policy',
            header: 'Policy',
            cell: ({ row }) => (
              <span className="text-sm text-neutral-400">{row.original.policy}</span>
            ),
          },
          {
            accessorKey: 'triggers',
            header: 'Triggers',
            cell: ({ row }) => (
              <div className="flex flex-wrap gap-1">
                {row.original.triggers.slice(0, 3).map((t) => (
                  <span key={t} className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-400">
                    {t}
                  </span>
                ))}
                {row.original.triggers.length > 3 && (
                  <span className="text-xs text-neutral-500">
                    +{row.original.triggers.length - 3}
                  </span>
                )}
              </div>
            ),
          },
          {
            accessorKey: 'source',
            header: 'Source',
            cell: ({ row }) => (
              <span className="text-sm text-neutral-500">
                {row.original.source || '—'}
              </span>
            ),
          },
          {
            accessorKey: 'createdAt',
            header: 'Created',
            cell: ({ row }) => (
              <span className="text-sm text-neutral-500">
                {new Date(row.original.createdAt).toLocaleDateString()}
              </span>
            ),
          },
        ]}
        data={filteredAgents}
        emptyMessage="No agents registered yet. Agents are auto-registered when they receive their first job."
      />
    </div>
  );
}

/* ── Summary Card ────────────────────────────────────────────────────────── */

function SummaryCard({
  title,
  value,
  sub,
}: {
  title: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <p className="text-sm text-neutral-400">{title}</p>
      <p className="mt-1 text-2xl font-bold text-neutral-100">{value}</p>
      <p className="mt-1 text-xs text-neutral-500">{sub}</p>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */

export function AgentListPage() {
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-100">Agents</h1>
      </div>

      <UnifiedAgentsTab />
    </div>
  );
}
